import * as Sentry from '@sentry/nextjs';

import sharedSentryOptions from './shared-sentry-options';
import {
    getOneTrustActiveGroups,
    hasConsentGroup,
    type OneTrustGroups,
    onOneTrustGroupsUpdated,
} from './src/scripts/utils';
import { holdSentryLogs, releaseHeldSentryLogs } from './src/utils/sentryLogQueue';

const PERFORMANCE_CONSENT_GROUP = 'C0002';

type SentryOptions = Parameters<typeof Sentry.init>[0];
type CreateSentryOptions = () => SentryOptions;
type ReplayRecordingMode = 'session' | 'buffer';
type SentryReplay = {
    stop: () => Promise<void>;
    start: () => void;
    startBuffering: () => void;
    getRecordingMode: () => ReplayRecordingMode | undefined;
};
type InitSentryClientParams = {
    createAppOptions: CreateSentryOptions;
    includeSharedOptions?: boolean;
};
type HasPerformanceConsent = () => boolean;
type ApplyConsentGuardParams<TEvent, THint> = {
    event: TEvent;
    hint: THint;
    hasPerformanceConsent: HasPerformanceConsent;
    callback?: (event: TEvent, hint: THint) => TEvent | null | PromiseLike<TEvent | null>;
};
type WithConsentGuardsParams = {
    options: SentryOptions;
    hasPerformanceConsent: HasPerformanceConsent;
};

function hasPerformanceConsentGroup(groups: OneTrustGroups): boolean {
    return hasConsentGroup({ group: PERFORMANCE_CONSENT_GROUP, groups });
}

function applyConsentGuard<TEvent, THint>(
    params: ApplyConsentGuardParams<TEvent, THint>
): TEvent | null | PromiseLike<TEvent | null> {
    const { event, hint, hasPerformanceConsent, callback } = params;

    if (!hasPerformanceConsent()) return null;

    if (!callback) return event;

    return callback(event, hint);
}

function withConsentGuards(params: WithConsentGuardsParams): SentryOptions {
    const { options, hasPerformanceConsent } = params;
    const { beforeBreadcrumb, beforeSend, beforeSendTransaction, tracesSampler, tracesSampleRate, ...restOptions } =
        options;

    const sentryOptions: SentryOptions = {
        ...restOptions,
        beforeBreadcrumb(breadcrumb, hint) {
            if (!hasPerformanceConsent()) return null;

            if (!beforeBreadcrumb) return breadcrumb;

            return beforeBreadcrumb(breadcrumb, hint);
        },
        beforeSend(event, hint) {
            return applyConsentGuard({ event, hint, hasPerformanceConsent, callback: beforeSend });
        },
        beforeSendTransaction(event, hint) {
            return applyConsentGuard({ event, hint, hasPerformanceConsent, callback: beforeSendTransaction });
        },
    };

    if (tracesSampler || tracesSampleRate !== undefined) {
        sentryOptions.tracesSampler = samplingContext => {
            if (!hasPerformanceConsent()) return 0;

            return tracesSampler?.(samplingContext) ?? tracesSampleRate ?? 0;
        };
    }

    return sentryOptions;
}

function createSentryOptions(params: {
    createAppOptions: CreateSentryOptions;
    includeSharedOptions: boolean;
}): SentryOptions {
    const { createAppOptions, includeSharedOptions } = params;
    const appOptions = createAppOptions();

    if (!includeSharedOptions) {
        return appOptions;
    }

    return {
        ...sharedSentryOptions,
        ...appOptions,
    };
}

/**
 * Browser Sentry is gated on OneTrust C0002 (Performance). Always on - no feature-flag bypass.
 * Init waits for consent; app options are lazy so integrations are not created early.
 * sentryLogger calls made before init are held (capped) and sent once consent starts Sentry.
 * After init, revoking C0002 drops new events/traces and stops Replay via the native API.
 */
function createSentryClientInitializer() {
    let sentryInitialized = false;
    let removeConsentListener: (() => void) | undefined;
    let hasCurrentPerformanceConsent = false;
    let currentCreateAppOptions: CreateSentryOptions | undefined;
    let currentIncludeSharedOptions = true;
    const replayState = {
        stoppedByConsent: false,
        modeAtRevoke: undefined as ReplayRecordingMode | undefined,
        stopPromise: undefined as Promise<void> | undefined,
    };

    const stopReplayForConsentRevoke = () => {
        if (replayState.stoppedByConsent) return;

        const replay = Sentry.getReplay?.() as SentryReplay | undefined;
        const replayRecordingMode = replay?.getRecordingMode();

        if (!replay || !replayRecordingMode) return;

        replayState.stoppedByConsent = true;
        replayState.modeAtRevoke = replayRecordingMode;

        // Sentry documents stop() as flushing pending data, stopping Replay,
        // and ending the Replay session:
        // https://docs.sentry.io/platforms/javascript/session-replay/understanding-sessions/
        const stopPromise = replay.stop().catch(() => undefined);
        replayState.stopPromise = stopPromise;

        void stopPromise.then(() => {
            if (replayState.stopPromise === stopPromise) {
                replayState.stopPromise = undefined;
            }
        });
    };

    const restartReplayStoppedByConsent = () => {
        if (!replayState.stoppedByConsent) return;

        const replay = Sentry.getReplay?.() as SentryReplay | undefined;

        if (!replay) return;

        const restartReplay = () => {
            if (!replayState.stoppedByConsent || !hasCurrentPerformanceConsent) return;

            if (replayState.modeAtRevoke === 'session') {
                replay.start();
            }

            if (replayState.modeAtRevoke === 'buffer') {
                replay.startBuffering();
            }

            replayState.stoppedByConsent = false;
            replayState.modeAtRevoke = undefined;
            replayState.stopPromise = undefined;
        };

        if (replayState.stopPromise) {
            void replayState.stopPromise.then(restartReplay);
            return;
        }

        restartReplay();
    };

    const startSentryOnce = () => {
        if (sentryInitialized) return;
        if (!currentCreateAppOptions) return;

        sentryInitialized = true;
        Sentry.init(
            withConsentGuards({
                options: createSentryOptions({
                    createAppOptions: currentCreateAppOptions,
                    includeSharedOptions: currentIncludeSharedOptions,
                }),
                hasPerformanceConsent: () => hasCurrentPerformanceConsent,
            })
        );
        releaseHeldSentryLogs();
    };

    const handleConsentGroups = (groups: OneTrustGroups) => {
        hasCurrentPerformanceConsent = hasPerformanceConsentGroup(groups);

        if (hasCurrentPerformanceConsent) {
            startSentryOnce();
            restartReplayStoppedByConsent();
            return;
        }

        if (sentryInitialized) {
            stopReplayForConsentRevoke();
        }
    };

    const startListeningForConsent = () => {
        if (removeConsentListener) return;

        removeConsentListener = onOneTrustGroupsUpdated(handleConsentGroups);
    };

    return {
        init(params: InitSentryClientParams) {
            const { createAppOptions, includeSharedOptions = true } = params;

            currentCreateAppOptions = createAppOptions;
            currentIncludeSharedOptions = includeSharedOptions;

            if (!sentryInitialized) {
                holdSentryLogs();
            }

            startListeningForConsent();
            handleConsentGroups(getOneTrustActiveGroups());
        },
    };
}

const sentryClientInitializer = createSentryClientInitializer();

export function initSentryClient(params: InitSentryClientParams): void {
    sentryClientInitializer.init(params);
}
